import { EnvxError, EnvxSchema, ParsedLine } from "../../types";

import {
  isSchemaHeader,
  findCommentIndex,
  handleMultilineLine,
} from "./helpers";

import { parseEnvLine, parseSchemaHeader, parseSchemaLine } from "./parser";

interface ParseResult {
  env: ParsedLine[];
  schema: EnvxSchema;
}

/**
 * Parses a custom `.envx` file format into structured environment variables and their schema.
 *
 * This parser supports:
 * - Standard key=value pairs, with automatic trimming and comment removal.
 * - Multiline values wrapped in triple quotes (`"""`), preserving line breaks.
 * - Optional schema sections defined via headers like `[VARNAME]`, with key-specific metadata.
 *
 * Example `.envx` content:
 * ```
 * API_KEY="abc123"
 *
 * [API_KEY]
 * type = string
 * required = true
 * ```
 *
 * @param {string} content - The raw string content of a `.envx` file to parse.
 * @returns {{ env: ParsedLine[], schema: EnvxSchema }} An object containing:
 *   - `env`: An array of parsed environment variables as `{ key, value }`.
 *   - `schema`: An object mapping variable names to their schema definitions.
 *
 * @throws {EnvxError} Throws if:
 *   - Syntax errors are encountered (e.g., malformed lines, invalid schema headers).
 *   - Inline comments appear improperly in schema headers.
 *   - Required attributes are missing from schema definitions.
 *
 * @remarks
 * The parser maintains state to handle multi-line strings seamlessly,
 * buffering lines until triple quotes close the value.
 * Schema parsing supports metadata like type, required flags, defaults, and deprecation.
 */
function parseEnvx(content: string): ParseResult {
  const lines = content.split(/\r?\n/);
  const env: ParsedLine[] = [];
  const schema: EnvxSchema = {};

  let currentSchemaKey: string | null = null;
  let multilineKey: string | null = null;
  let multilineBuffer: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    let originalLine = lines[i];

    let trimmedLine = originalLine.trim();
    if (!trimmedLine || trimmedLine.startsWith("#")) continue;
    if (multilineKey) {
      const result = handleMultilineLine(
        originalLine,
        multilineKey,
        multilineBuffer,
        env
      );

      multilineBuffer = result.multilineBuffer;
      multilineKey = result.multilineKey;
      continue;
    }

    // >_ Remove inline comments
    const commentIndex = findCommentIndex(originalLine);
    if (commentIndex !== -1) {
      originalLine = originalLine.slice(0, commentIndex);
    }
    trimmedLine = originalLine.trim();

    if (isSchemaHeader(trimmedLine)) {
      if (commentIndex > -1 && commentIndex > originalLine.indexOf("]")) {
        throw new EnvxError(
          `[envx:error] Line ${
            i + 1
          }: Schema header line cannot contain inline comments.`
        );
      }
      currentSchemaKey = parseSchemaHeader(trimmedLine, i, schema);
      continue;
    }

    if (currentSchemaKey) {
      parseSchemaLine(trimmedLine, i, schema[currentSchemaKey], () => {
        i++;
        if (i >= lines.length) return null;
        let nextLine = lines[i].trim();
        return nextLine;
      });
      continue;
    }

    const { key, value, isMultilineStart } = parseEnvLine(trimmedLine, i);
    if (isMultilineStart) {
      multilineKey = key;
      multilineBuffer.push(value);
    } else {
      env.push({ key, value });
    }
  }

  if (multilineKey) {
    throw new EnvxError(
      `[envx:error] End of file reached but multiline value for key "${multilineKey}" was not closed with triple quotes (""").`
    );
  }
  return { env, schema };
}

export { parseEnvx };
